import { getValidationErrorKey } from '@api/helpers';
import { ValidationErrors } from '@api/interfaces';
import { FormErrors } from '@hooks/useTrackFormChanges/interfaces';
import {
  FormErrors as AddressFormErrors,
  FormAddress,
} from '@components/form-components/AddressFieldSet/interfaces';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import i18n from '@translations/i18n';

export const isValidEmailAddress = (email: string): boolean => {
  return (
    email
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      ) !== null
  );
};

export const isValidUrl = (url: string): boolean => {
  try {
    const parsedUrl = new URL(url);

    return ['http:', 'https:'].includes(parsedUrl.protocol);
  } catch {
    return false;
  }
};

// TODO: The external number validation needs to be done in the backend when we support different types of associations.
export const isValidExternalNumber = (externalId: string): boolean => {
  return externalId.toLowerCase().match(/^\d{4,6}$/) !== null;
};

export const isNotEmpty = (str: string | undefined): boolean => {
  return str != undefined && str.trim().length > 0;
};

export const isEmpty = (str: string | undefined): boolean => {
  return !isNotEmpty(str);
};

export const atLeast = (str: string, count: number): boolean => {
  return str.trim().length >= count;
};

export const maxChars = (str: string, max: number): boolean => {
  return str.trim().length <= max;
};

export const isAtLeastAge = (
  birthDate: dayjs.Dayjs,
  minAge: number
): boolean => {
  return dayjs().diff(birthDate, 'year') >= minAge;
};

export const isValidStreet = (value?: string): boolean =>
  isNotEmpty(value) && /^[\p{L}\p{N}\s.'’/-]+$/u.test(value!);

export const isValidHouseNumber = (value?: string): boolean =>
  isNotEmpty(value) && /^\d+[A-Za-z0-9/-]*$/.test(value!);

export const isValidHouseNumberSuffix = (value: string): boolean =>
  /^[A-Za-z0-9\s./-]+$/.test(value);

export const isValidPostalCode = (value?: string): boolean =>
  isNotEmpty(value) && /^[A-Za-z0-9\s-]+$/.test(value!);

export const isValidCity = (value?: string): boolean =>
  isNotEmpty(value) && /^[\p{L}\s.'’-]+$/u.test(value!);

export const isValidCountry = (value?: string): boolean =>
  isNotEmpty(value) && /^[\p{L}\s.'’-]+$/u.test(value!);

export const isValidAddressExtra = (value: string): boolean =>
  /^[\p{L}\p{N}\s.'’,/#()-]+$/u.test(value);

export const isAddressFilled = (address: FormAddress): boolean => {
  if (!address) {
    return false;
  }

  return (
    isNotEmpty(address.street) &&
    isNotEmpty(address.houseNumber) &&
    isNotEmpty(address.postalCode) &&
    isNotEmpty(address.city) &&
    isNotEmpty(address.countryCode)
  );
};

export const isAddressDirty = (
  address: FormAddress | undefined | null
): boolean => {
  if (!address) {
    return false;
  }

  return (
    isNotEmpty(address.street) ||
    isNotEmpty(address.houseNumber) ||
    isNotEmpty(address.houseNumberSuffix) ||
    isNotEmpty(address.postalCode) ||
    isNotEmpty(address.city) ||
    isNotEmpty(address.addressExtra) ||
    isNotEmpty(address.countryCode)
  );
};

export const validateAddress = (
  address: FormAddress
): AddressFormErrors | undefined => {
  if (
    (address && Object.keys(address).length === 0) ||
    !isAddressDirty(address)
  ) {
    return undefined;
  }

  const errors = {} as AddressFormErrors;

  if (isEmpty(address.street)) {
    errors.street = i18n.t('errors.required');
  } else if (!isValidStreet(address.street)) {
    errors.street = i18n.t('errors.invalidStreet');
  }

  if (isEmpty(address.houseNumber)) {
    errors.houseNumber = i18n.t('errors.required');
  } else if (!isValidHouseNumber(address.houseNumber)) {
    errors.houseNumber = i18n.t('errors.invalidHouseNumber');
  }

  if (isEmpty(address.postalCode)) {
    errors.postalCode = i18n.t('errors.required');
  } else if (!isValidPostalCode(address.postalCode)) {
    errors.postalCode = i18n.t('errors.invalidPostalCode');
  }

  if (isEmpty(address.city)) {
    errors.city = i18n.t('errors.required');
  } else if (!isValidCity(address.city)) {
    errors.city = i18n.t('errors.invalidCity');
  }

  if (isEmpty(address.countryCode)) {
    errors.country = i18n.t('errors.required');
  } else if (!isValidCountry(address.countryCode)) {
    errors.country = i18n.t('errors.invalidCountry');
  }

  // OPTIONAL FIELDS
  if (
    isNotEmpty(address.houseNumberSuffix) &&
    !isValidHouseNumberSuffix(address.houseNumberSuffix!)
  ) {
    errors.houseNumberSuffix = i18n.t('errors.invalidHouseNumberSuffix');
  }

  if (
    isNotEmpty(address.addressExtra) &&
    !isValidAddressExtra(address.addressExtra!)
  ) {
    errors.addressExtra = i18n.t('errors.invalidAddressExtra');
  }

  return Object.keys(errors).length !== 0 ? errors : undefined;
};

export const processFormValidationErrors = (
  errorResponse: AxiosError<ValidationErrors>,
  setFormErrors: React.Dispatch<React.SetStateAction<FormErrors>>,
  formFields: string[],
  onNoErrors: () => void
): void => {
  if (errorResponse.response?.status === 422) {
    const foundErrors: FormErrors = {};
    const validationErrors = errorResponse.response.data.errors;

    Object.entries(validationErrors).forEach(([field, messages]) => {
      if (!formFields.includes(field)) {
        return;
      }

      const errorKey = getValidationErrorKey(messages[0]);

      if (errorKey) {
        foundErrors[field] = i18n.t(`errors.${errorKey}`);
      }
    });

    if (Object.keys(foundErrors).length > 0) {
      setFormErrors(foundErrors);
    } else {
      onNoErrors();
    }
  }
};
